import { create } from 'zustand';
import axios from 'axios';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuthStore = create((set, get) => ({
  user: null,
  token: localStorage.getItem('token'),
  isLoading: true,
  isAuthenticated: false,
  isImpersonating: !!localStorage.getItem('admin_original_token'),

  login: async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    // Set language from user preference
    if (user.preferred_language) {
      i18n.changeLanguage(user.preferred_language);
      localStorage.setItem('language', user.preferred_language);
    }
    set({ token, user, isAuthenticated: true, isLoading: false, isImpersonating: false });
    return user;
  },

  register: async (email, password, name, language = 'es') => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      name,
      preferred_language: language,
    });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    // Set language from registration choice
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
    set({ token, user, isAuthenticated: true, isLoading: false, isImpersonating: false });
    return user;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_original_token');
    set({ user: null, token: null, isAuthenticated: false, isImpersonating: false });
  },

  // Set auth directly (used for impersonation)
  setAuth: (token, user) => {
    localStorage.setItem('token', token);
    set({ token, user, isAuthenticated: true, isLoading: false, isImpersonating: !!localStorage.getItem('admin_original_token') });
  },

  // Stop impersonating and return to admin
  stopImpersonating: async () => {
    const adminToken = localStorage.getItem('admin_original_token');
    if (!adminToken) return;
    
    try {
      // Load admin user data
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('admin_original_token');
      
      set({ 
        token: adminToken, 
        user: response.data, 
        isAuthenticated: true, 
        isLoading: false,
        isImpersonating: false 
      });
      
      return response.data;
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      localStorage.removeItem('admin_original_token');
      set({ isImpersonating: false });
    }
  },

  loadUser: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      set({ isLoading: false });
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const user = response.data;
      // Set language from user preference on page reload
      if (user.preferred_language) {
        i18n.changeLanguage(user.preferred_language);
        localStorage.setItem('language', user.preferred_language);
      }
      set({ 
        user, 
        token, 
        isAuthenticated: true, 
        isLoading: false,
        isImpersonating: !!localStorage.getItem('admin_original_token')
      });
    } catch (error) {
      localStorage.removeItem('token');
      set({ user: null, token: null, isAuthenticated: false, isLoading: false, isImpersonating: false });
    }
  },

  updateUser: async (data) => {
    const { token } = get();
    const response = await axios.put(`${API_URL}/api/auth/me`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    set({ user: response.data });
    return response.data;
  },

  getAuthHeaders: () => {
    const { token } = get();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
}));
